import { useEffect, useState, useMemo } from 'react'
import { favoriteService, FavoriteType } from '@/services/favoriteService'
import { DetailHeader } from '@/components/ui/DetailHeader'
import DataTable from '@/components/ui/DataTable'
import FilterBar from '@/components/ui/FilterBar'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { RefreshCw, Heart, Store, Package, ShoppingBag, TrendingUp } from 'lucide-react'

interface PopularFavorite {
  type: FavoriteType
  item_id: string
  item_name: string
  item_description?: string | null
  item_image?: string | null
  favorite_count: number
}

const FavoritesPage = () => {
  const [popularFavorites, setPopularFavorites] = useState<PopularFavorite[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilters, setActiveFilters] = useState<any[]>([])
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  })

  const loadPopularFavorites = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await favoriteService.getPopularFavorites(50)
      if (response.success) {
        setPopularFavorites(response.data)
        setPagination(prev => ({
          ...prev,
          total: response.data.length
        }))
      } else {
        setError('Échec du chargement des favoris populaires')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPopularFavorites()
  }, [])

  const getTypeLabel = (type: FavoriteType) => {
    switch (type) {
      case 'merchant': return 'Commerçant'
      case 'product': return 'Produit'
      case 'basket': return 'Panier'
      default: return type
    }
  }

  const getTypeIcon = (type: FavoriteType) => {
    switch (type) {
      case 'merchant': return <Store className="h-3 w-3 mr-1" />
      case 'product': return <Package className="h-3 w-3 mr-1" />
      case 'basket': return <ShoppingBag className="h-3 w-3 mr-1" />
      default: return <Heart className="h-3 w-3 mr-1" />
    }
  }

  const getTypeBadgeVariant = (type: FavoriteType) => {
    switch (type) {
      case 'merchant': return 'default'
      case 'product': return 'secondary'
      case 'basket': return 'outline'
      default: return 'default'
    }
  }

  const columns = useMemo(() => [
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      render: (value: FavoriteType) => (
        <Badge variant={getTypeBadgeVariant(value)} className="inline-flex items-center">
          {getTypeIcon(value)}
          {getTypeLabel(value)}
        </Badge>
      )
    },
    {
      key: 'item_name',
      label: 'Nom',
      sortable: true,
      render: (value: string, row: PopularFavorite) => (
        <div className="font-medium">
          <div className="flex items-center">
            <Heart className="h-4 w-4 mr-2 text-red-500" />
            {value}
          </div>
          {row.item_description && (
            <div className="text-sm text-gray-500 truncate max-w-xs">
              {row.item_description}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'favorite_count',
      label: 'Nombre de favoris',
      sortable: true,
      render: (value: number) => (
        <div className="flex items-center">
          <span className="font-bold text-lg">{value}</span>
          <span className="ml-2 text-gray-500 text-sm">favoris</span>
        </div>
      )
    },
    {
      key: 'item_id',
      label: 'ID de l\'élément',
      sortable: true,
      render: (value: string) => (
        <div className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
          {value.substring(0, 8)}...
        </div>
      )
    }
  ], [])

  const actions = useMemo(() => [
    {
      label: 'Voir',
      icon: <Heart className="h-4 w-4" />,
      onClick: (row: PopularFavorite) => console.log('View favorite', row.item_id)
    },
    {
      label: 'Analyser',
      icon: <TrendingUp className="h-4 w-4" />,
      onClick: (row: PopularFavorite) => console.log('Analyze favorite', row.item_id)
    }
  ], [])

  const filterOptions = useMemo(() => [
    {
      key: 'type',
      label: 'Type',
      type: 'select' as const,
      options: [
        { value: 'all', label: 'Tous les types' },
        { value: 'merchant', label: 'Commerçant' },
        { value: 'product', label: 'Produit' },
        { value: 'basket', label: 'Panier' }
      ]
    }
  ], [])

  const handleFilterChange = (newFilters: any[]) => {
    setActiveFilters(newFilters)
  }

  const handleSearch = (value: string) => {
    setSearchQuery(value)
  }

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }))
  }

  const handlePageSizeChange = (newSize: number) => {
    setPagination(prev => ({ ...prev, pageSize: newSize, page: 1 }))
  }

  const filteredData = useMemo(() => {
    let result = popularFavorites

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(favorite => 
        favorite.item_name.toLowerCase().includes(query) || 
        (favorite.item_description?.toLowerCase() || '').includes(query) ||
        favorite.item_id.toLowerCase().includes(query)
      )
    }

    // Apply other filters
    activeFilters.forEach(filter => {
      if (filter.key === 'type' && filter.value !== 'all') {
        result = result.filter(favorite => favorite.type === filter.value)
      }
    })

    // Update pagination total
    setPagination(prev => ({ ...prev, total: result.length }))

    return result
  }, [popularFavorites, searchQuery, activeFilters])

  const paginatedData = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize
    const endIndex = startIndex + pagination.pageSize
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, pagination.page, pagination.pageSize])

  const breadcrumbs = [
    { label: 'Tableau de bord', href: '/' },
    { label: 'Favoris', href: '/favorites' }
  ]

  const headerActions = [
    {
      label: 'Actualiser',
      icon: <RefreshCw className="h-4 w-4 mr-2" />,
      onClick: loadPopularFavorites,
      variant: 'outline' as const,
      disabled: loading
    }
  ]

  const stats = useMemo(() => {
    const totalFavorites = popularFavorites.reduce((sum, f) => sum + f.favorite_count, 0)
    const byType = {
      merchant: popularFavorites.filter(f => f.type === 'merchant').length,
      product: popularFavorites.filter(f => f.type === 'product').length,
      basket: popularFavorites.filter(f => f.type === 'basket').length
    }
    
    return {
      total: popularFavorites.length,
      totalFavorites,
      byType
    }
  }, [popularFavorites])

  const topFavorites = useMemo(() => 
    [...popularFavorites]
      .sort((a, b) => b.favorite_count - a.favorite_count)
      .slice(0, 3),
    [popularFavorites]
  )

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Favoris"
        subtitle="Gérez les favoris des utilisateurs"
        breadcrumbs={breadcrumbs}
        actions={headerActions}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
          <div className="mt-4">
            <Button variant="outline" onClick={loadPopularFavorites}>
              Réessayer
            </Button>
          </div>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Éléments populaires</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Favoris totaux</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalFavorites}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Répartition par type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(stats.byType).map(([type, count]) => (
                <div key={type} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{getTypeLabel(type as FavoriteType)}</span>
                  <span className="font-bold">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <FilterBar
        searchPlaceholder="Rechercher par nom, description ou ID..."
        filters={filterOptions}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
      />

      <DataTable
        columns={columns}
        data={paginatedData}
        actions={actions}
        loading={loading}
        emptyMessage="Aucun favori trouvé"
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange
        }}
      />

      {/* Additional Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Statistiques des favoris</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-500 mb-4">
            Cette page affiche les éléments les plus favorisés par les utilisateurs.
            Pour voir les favoris d'un utilisateur spécifique, utilisez la page Utilisateurs.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-medium">Top 3 des favoris</h3>
              {topFavorites.map((favorite, index) => (
                <div key={favorite.item_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="h-8 w-8 flex items-center justify-center bg-blue-100 text-blue-700 rounded-md mr-3">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium">{favorite.item_name}</div>
                      <div className="text-sm text-gray-500">{getTypeLabel(favorite.type)}</div>
                    </div>
                  </div>
                  <div className="font-bold">{favorite.favorite_count} favoris</div>
                </div>
              ))}
            </div>
            <div className="space-y-4">
              <h3 className="font-medium">Répartition par type</h3>
              <div className="space-y-3">
                {Object.entries(stats.byType).map(([type, count]) => {
                  const percentage = stats.total > 0 
                    ? Math.round((count / stats.total) * 100) 
                    : 0
                  return (
                    <div key={type}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm">{getTypeLabel(type as FavoriteType)}</span>
                        <span className="text-sm font-medium">{count} ({percentage}%)</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default FavoritesPage
